import React, { useState, useEffect } from 'react';
import { Calendar, Building2, Home, Download, FileSpreadsheet, FileText, TrendingUp, TrendingDown, Package, ChevronDown, ChevronRight, Maximize2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { fetchEntregasReporte } from '@/services/entregasLenceria.service.js';
import { fetchEdificios } from '@/services/edificios.service.js';
import { fetchHospedajes } from '@/services/hospedajes.service.js';
import * as XLSX from 'xlsx-js-style';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const ReporteEntregasHorizontal = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [entregas, setEntregas] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [hospedajes, setHospedajes] = useState([]);
  const [filteredHospedajes, setFilteredHospedajes] = useState([]);

  // Filtros
  const [filters, setFilters] = useState({
    fechaInicio: format(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd'),
    fechaFin: format(new Date(), 'yyyy-MM-dd'),
    edificioId: '',
    hospedajeId: ''
  });

  // Estadísticas
  const [stats, setStats] = useState({
    totalEntregas: 0,
    totalRecolecciones: 0,
    pesoTotalEntregado: 0,
    pesoTotalRecolectado: 0,
    itemsTotalesEntregados: 0,
    itemsTotalesRecolectados: 0
  });

  // Kardex de prendas
  const [kardex, setKardex] = useState([]);

  // Kardex detallado (matriz)
  const [kardexDetallado, setKardexDetallado] = useState([]);

  // Comparativo por edificio (resumen + cruce de prendas)
  const [comparativoEdificios, setComparativoEdificios] = useState({
    resumen: [],
    prendas: []
  });

  // Estado para filas expandidas
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Estado para modal de kardex detallado
  const [showKardexModal, setShowKardexModal] = useState(false);

  // Efecto para prevenir scroll del body cuando el modal está abierto
  useEffect(() => {
    if (showKardexModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showKardexModal]);

  useEffect(() => {
    loadEdificios();
    loadHospedajes();
  }, []);

  useEffect(() => {
    if (filters.edificioId) {
      const filtered = hospedajes.filter(h => h.edificio?.id === filters.edificioId);
      setFilteredHospedajes(filtered);
    } else {
      setFilteredHospedajes(hospedajes);
    }
  }, [filters.edificioId, hospedajes]);

  const loadEdificios = async () => {
    try {
      const query = {
        tipoLogistica: 'horizontal'
      }
      const response = await fetchEdificios({ query });
      const data = response?.data || response || [];
      setEdificios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar edificios:', error);
      setEdificios([]);
    }
  };

  const exportKardexDetalladoToExcel = () => {
    try {
      if (!kardexDetallado?.prendas || kardexDetallado.prendas.length === 0) {
        toast({
          title: 'Sin datos',
          description: 'No hay datos para exportar en el kardex detallado',
          variant: 'destructive',
        });
        return;
      }

      // Encabezado principal (fila 1): Tipo + consecutivo
      const headerRow1 = ['Prenda'];
      // Encabezado secundario (fila 2): Edificio + Hospedaje + fecha
      const headerRow2 = [''];
      // Anchos de columna: la primera más ancha para nombres de prenda
      const colWidths = [{ wch: 30 }];

      kardexDetallado.transacciones.forEach(trans => {
        const tipoLabel = trans.tipo === 'entrega' ? 'Entrega' : 'Recolección';
        const codigo = `${trans.tipo === 'entrega' ? 'E' : 'R'}-${trans.consecutivo || 'N/A'}`;
        headerRow1.push(`${tipoLabel}\n${codigo}`);
        const subHeader = [
          trans.edificio || 'Sin edificio',
          trans.hospedaje || 'Sin activo',
          format(new Date(trans.fecha), 'dd/MM/yyyy'),
        ].filter(Boolean).join('\n');
        headerRow2.push(subHeader);
        colWidths.push({ wch: 24 });
      });
      headerRow1.push('Saldo Final');
      headerRow2.push('');
      colWidths.push({ wch: 14 });

      const matrixData = [headerRow1, headerRow2];
      kardexDetallado.prendas.forEach(row => {
        const rowData = [row.prenda];
        row.transacciones.forEach(trans => {
          if (trans.movimiento !== 0) {
            rowData.push(`${trans.movimiento > 0 ? '+' : ''}${trans.movimiento} (${trans.saldo})`);
          } else {
            rowData.push('-');
          }
        });
        rowData.push(row.saldoFinal);
        matrixData.push(rowData);
      });

      const wb = XLSX.utils.book_new();
      const wsKardexDetallado = XLSX.utils.aoa_to_sheet(matrixData);

      // Aplicar anchos de columna
      wsKardexDetallado['!cols'] = colWidths;

      // Combinar celdas: Prenda y Saldo Final en ambas filas
      const merges = [
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Prenda
        { s: { r: 0, c: headerRow1.length - 1 }, e: { r: 1, c: headerRow1.length - 1 } }, // Saldo Final
      ];
      wsKardexDetallado['!merges'] = merges;

      // Estilo de los encabezados: wrap text + alineación centrada + negrita
      const range = XLSX.utils.decode_range(wsKardexDetallado['!ref']);
      for (let R = 0; R <= 1; R++) {
        for (let C = 0; C <= range.e.c; C++) {
          const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
          if (wsKardexDetallado[cellRef]) {
            wsKardexDetallado[cellRef].s = {
              font: { bold: true, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: '1F2937' } }, // gris oscuro
              alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
              border: {
                top: { style: 'thin', color: { rgb: 'D1D5DB' } },
                bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
                left: { style: 'thin', color: { rgb: 'D1D5DB' } },
                right: { style: 'thin', color: { rgb: 'D1D5DB' } },
              },
            };
          }
        }
      }

      // Ajustar altura de las filas de encabezado
      wsKardexDetallado['!rows'] = [{ hpt: 30 }, { hpt: 30 }];

      // Bordes y centrado para el resto de las celdas
      for (let R = 2; R <= range.e.r; R++) {
        for (let C = 0; C <= range.e.c; C++) {
          const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
          if (wsKardexDetallado[cellRef]) {
            wsKardexDetallado[cellRef].s = {
              alignment: { horizontal: C === 0 ? 'left' : 'center', vertical: 'center', wrapText: true },
              border: {
                top: { style: 'thin', color: { rgb: 'E5E7EB' } },
                bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
                left: { style: 'thin', color: { rgb: 'E5E7EB' } },
                right: { style: 'thin', color: { rgb: 'E5E7EB' } },
              },
            };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, wsKardexDetallado, 'Kardex Detallado');

      const fileName = `Kardex_Detallado_Horizontal_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast({
        title: 'Exportación exitosa',
        description: `Archivo ${fileName} descargado`,
      });
    } catch (error) {
      console.error('Error al exportar kardex detallado:', error);
      toast({
        title: 'Error',
        description: 'No se pudo exportar el kardex detallado',
        variant: 'destructive',
      });
    }
  };

  const loadHospedajes = async () => {
    try {
      const response = await fetchHospedajes();
      const data = response?.data || response || [];
      setHospedajes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error al cargar hospedajes:', error);
      setHospedajes([]);
    }
  };

  const loadEntregas = async () => {
    setLoading(true);
    try {
      const filterData = {
        fechaInicio: filters.fechaInicio,
        fechaFin: filters.fechaFin,
        estado: ['entregado', 'recoleccion'],
        limit: 10000
      };

      if (filters.edificioId) {
        filterData.edificioId = filters.edificioId;
      }

      if (filters.hospedajeId) {
        filterData.hospedajeId = filters.hospedajeId;
      }

      const response = await fetchEntregasReporte(filterData);
      // La respuesta tiene estructura: { data: { entregas: [], total: X } }
      const entregas = response?.data?.entregas || response?.entregas || [];
      setEntregas(entregas);
      calculateStats(entregas);

      toast({
        title: 'Datos cargados',
        description: `Se encontraron ${entregas.length} registros`,
      });
    } catch (error) {
      console.error('Error al cargar entregas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const entregas = data.filter(e => e.estado === 'entregado');
    const recolecciones = data.filter(e => e.estado === 'recoleccion');

    setStats({
      totalEntregas: entregas.length,
      totalRecolecciones: recolecciones.length,
      pesoTotalEntregado: entregas.reduce((sum, e) => sum + (e.peso_total || 0), 0),
      pesoTotalRecolectado: recolecciones.reduce((sum, e) => sum + (e.peso_total || 0), 0),
      itemsTotalesEntregados: entregas.reduce((sum, e) => sum + (e.total_items || 0), 0),
      itemsTotalesRecolectados: recolecciones.reduce((sum, e) => sum + (e.total_items || 0), 0)
    });

    // Calcular kardex de prendas
    calculateKardex(data);

    // Calcular comparativo por edificio
    calculateComparativoEdificios(data);
  };

  const calculateComparativoEdificios = (data) => {
    // Mapa por edificio: { id -> { nombre, entregas, recolecciones, ... } }
    const edificiosMap = new Map();

    data.forEach(entrega => {
      const edificioId = entrega.edificio?._id || entrega.edificio?.id || 'sin-edificio';
      const edificioNombre = entrega.edificio?.nombre || 'Sin edificio';

      if (!edificiosMap.has(edificioId)) {
        edificiosMap.set(edificioId, {
          id: edificioId,
          nombre: edificioNombre,
          totalEntregas: 0,
          totalRecolecciones: 0,
          pesoTotalEntregado: 0,
          pesoTotalRecolectado: 0,
          itemsTotalesEntregados: 0,
          itemsTotalesRecolectados: 0,
          // Acumuladores por prenda
          prendas: new Map()
        });
      }

      const ed = edificiosMap.get(edificioId);
      const isEntrega = entrega.estado === 'entregado';

      if (isEntrega) {
        ed.totalEntregas += 1;
        ed.pesoTotalEntregado += (entrega.peso_total || 0);
        ed.itemsTotalesEntregados += (entrega.total_items || 0);
      } else {
        ed.totalRecolecciones += 1;
        ed.pesoTotalRecolectado += (entrega.peso_total || 0);
        ed.itemsTotalesRecolectados += (entrega.total_items || 0);
      }

      // Acumular por prenda
      (entrega.items || []).forEach(item => {
        if (!ed.prendas.has(item.nombre)) {
          ed.prendas.set(item.nombre, {
            nombre: item.nombre,
            entradas: 0,
            salidas: 0
          });
        }
        const p = ed.prendas.get(item.nombre);
        if (isEntrega) {
          p.salidas += item.cantidad;
        } else {
          p.entradas += item.cantidad;
        }
      });
    });

    // Construir resumen con saldos
    const resumen = Array.from(edificiosMap.values()).map(ed => ({
      id: ed.id,
      nombre: ed.nombre,
      totalEntregas: ed.totalEntregas,
      totalRecolecciones: ed.totalRecolecciones,
      pesoTotalEntregado: ed.pesoTotalEntregado,
      pesoTotalRecolectado: ed.pesoTotalRecolectado,
      itemsTotalesEntregados: ed.itemsTotalesEntregados,
      itemsTotalesRecolectados: ed.itemsTotalesRecolectados,
      saldoEntregas: ed.totalEntregas - ed.totalRecolecciones,
      saldoPeso: ed.pesoTotalEntregado - ed.pesoTotalRecolectado,
      saldoItems: ed.itemsTotalesEntregados - ed.itemsTotalesRecolectados
    }));

    // Ordenar por nombre de edificio
    resumen.sort((a, b) => a.nombre.localeCompare(b.nombre));

    // Construir tabla cruzada: filas = prendas, columnas = edificios (Entradas/Salidas/Saldo por edificio)
    const prendasSet = new Set();
    edificiosMap.forEach(ed => {
      ed.prendas.forEach((_, nombre) => prendasSet.add(nombre));
    });
    const prendasOrdenadas = Array.from(prendasSet).sort();

    const edificiosOrdenados = Array.from(edificiosMap.values())
      .sort((a, b) => a.nombre.localeCompare(b.nombre));

    const prendas = prendasOrdenadas.map(prenda => {
      const row = { prenda };
      edificiosOrdenados.forEach(ed => {
        const p = ed.prendas.get(prenda);
        const entradas = p?.entradas || 0;
        const salidas = p?.salidas || 0;
        row[`${ed.nombre}_Entradas`] = entradas;
        row[`${ed.nombre}_Salidas`] = salidas;
        row[`${ed.nombre}_Saldo`] = entradas - salidas;
      });
      return row;
    });

    setComparativoEdificios({
      resumen,
      prendas,
      nombresEdificios: edificiosOrdenados.map(e => e.nombre)
    });
  };

  const calculateKardex = (data) => {
    const kardexMap = new Map();

    data.forEach(entrega => {
      const isEntrega = entrega.estado === 'entregado';
      const items = entrega.items || [];

      items.forEach(item => {
        const key = item.nombre;
        if (!kardexMap.has(key)) {
          kardexMap.set(key, {
            nombre: item.nombre,
            entradas: 0,
            salidas: 0,
            pesoUnitario: item.peso || 0,
            pesoTotalEntradas: 0,
            pesoTotalSalidas: 0
          });
        }

        const kardexItem = kardexMap.get(key);
        if (isEntrega) {
          kardexItem.salidas += item.cantidad;
          kardexItem.pesoTotalSalidas += (item.cantidad * (item.peso || 0));
        } else {
          kardexItem.entradas += item.cantidad;
          kardexItem.pesoTotalEntradas += (item.cantidad * (item.peso || 0));
        }
      });
    });

    // Convertir a array y calcular saldo
    const kardexArray = Array.from(kardexMap.values()).map(item => ({
      ...item,
      saldo: item.entradas - item.salidas,
      pesoSaldo: item.pesoTotalEntradas - item.pesoTotalSalidas
    }));

    // Ordenar por nombre
    kardexArray.sort((a, b) => a.nombre.localeCompare(b.nombre));

    setKardex(kardexArray);

    // Calcular kardex detallado (matriz)
    calculateKardexDetallado(data);
  };

  const calculateKardexDetallado = (data) => {
    // Ordenar entregas por fecha
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.fecha_entrega || a.createdAt);
      const dateB = new Date(b.fecha_entrega || b.createdAt);
      return dateA - dateB;
    });

    // Obtener todas las prendas únicas
    const prendasSet = new Set();
    sortedData.forEach(entrega => {
      entrega.items?.forEach(item => {
        prendasSet.add(item.nombre);
      });
    });

    const prendas = Array.from(prendasSet).sort();

    // Crear matriz de kardex
    const kardexMatrix = prendas.map(prenda => {
      const row = {
        prenda,
        transacciones: [],
        saldoFinal: 0
      };

      let saldoAcumulado = 0;

      sortedData.forEach(entrega => {
        const item = entrega.items?.find(i => i.nombre === prenda);

        if (item) {
          const isEntrega = entrega.estado === 'entregado';
          const cantidad = item.cantidad;
          const movimiento = isEntrega ? -cantidad : cantidad; // Entregas restan, Recolecciones suman
          saldoAcumulado += movimiento;

          row.transacciones.push({
            id: entrega._id,
            consecutivo: entrega.consecutivo_entrega || entrega.consecutivo_recoleccion,
            tipo: isEntrega ? 'entrega' : 'recoleccion',
            fecha: entrega.fecha_entrega || entrega.createdAt,
            cantidad: item.cantidad,
            movimiento, // Positivo para recolecciones, negativo para entregas
            saldo: saldoAcumulado,
            edificio: entrega.edificio?.nombre,
            hospedaje: entrega.hospedaje?.nombre
          });
        } else {
          // No hay movimiento de esta prenda en esta transacción
          row.transacciones.push({
            id: entrega._id,
            consecutivo: entrega.consecutivo_entrega || entrega.consecutivo_recoleccion,
            tipo: entrega.estado === 'entregado' ? 'entrega' : 'recoleccion',
            fecha: entrega.fecha_entrega || entrega.createdAt,
            cantidad: 0,
            movimiento: 0,
            saldo: saldoAcumulado,
            edificio: entrega.edificio?.nombre,
            hospedaje: entrega.hospedaje?.nombre
          });
        }
      });

      row.saldoFinal = saldoAcumulado;
      return row;
    });

    setKardexDetallado({
      prendas: kardexMatrix,
      transacciones: sortedData.map(e => ({
        id: e._id,
        consecutivo: e.consecutivo_entrega || e.consecutivo_recoleccion,
        tipo: e.estado === 'entregado' ? 'entrega' : 'recoleccion',
        fecha: e.fecha_entrega || e.createdAt,
        edificio: e.edificio?.nombre,
        hospedaje: e.hospedaje?.nombre
      }))
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'edificioId' && value === '') {
      setFilters(prev => ({ ...prev, hospedajeId: '' }));
    }
  };

  const toggleRowExpansion = (entregaId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entregaId)) {
        newSet.delete(entregaId);
      } else {
        newSet.add(entregaId);
      }
      return newSet;
    });
  };

  const buildComparativoEdificiosWorksheets = () => {
    const sheets = [];

    if (!comparativoEdificios.resumen || comparativoEdificios.resumen.length === 0) {
      return sheets;
    }

    // ====== HOJA 1: Resumen por edificio ======
    const resumenData = [
      ['COMPARATIVO POR EDIFICIO'],
      [`Período: ${filters.fechaInicio} a ${filters.fechaFin}`],
      [''],
      ['Edificio', 'Total Entregas', 'Total Recolecciones', 'Saldo Transacciones',
        'Peso Entregado (kg)', 'Peso Recolectado (kg)', 'Saldo Peso (kg)',
        'Items Entregados', 'Items Recolectados', 'Saldo Items']
    ];

    comparativoEdificios.resumen.forEach(r => {
      resumenData.push([
        r.nombre,
        r.totalEntregas,
        r.totalRecolecciones,
        r.saldoEntregas,
        Number((r.pesoTotalEntregado / 1000).toFixed(2)),
        Number((r.pesoTotalRecolectado / 1000).toFixed(2)),
        Number((r.saldoPeso / 1000).toFixed(2)),
        r.itemsTotalesEntregados,
        r.itemsTotalesRecolectados,
        r.saldoItems
      ]);
    });

    // Fila de totales
    resumenData.push([
      'TOTALES',
      comparativoEdificios.resumen.reduce((s, r) => s + r.totalEntregas, 0),
      comparativoEdificios.resumen.reduce((s, r) => s + r.totalRecolecciones, 0),
      comparativoEdificios.resumen.reduce((s, r) => s + r.saldoEntregas, 0),
      Number((comparativoEdificios.resumen.reduce((s, r) => s + r.pesoTotalEntregado, 0) / 1000).toFixed(2)),
      Number((comparativoEdificios.resumen.reduce((s, r) => s + r.pesoTotalRecolectado, 0) / 1000).toFixed(2)),
      Number((comparativoEdificios.resumen.reduce((s, r) => s + r.saldoPeso, 0) / 1000).toFixed(2)),
      comparativoEdificios.resumen.reduce((s, r) => s + r.itemsTotalesEntregados, 0),
      comparativoEdificios.resumen.reduce((s, r) => s + r.itemsTotalesRecolectados, 0),
      comparativoEdificios.resumen.reduce((s, r) => s + r.saldoItems, 0),
    ]);

    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    wsResumen['!cols'] = [
      { wch: 28 }, { wch: 14 }, { wch: 16 }, { wch: 16 },
      { wch: 18 }, { wch: 18 }, { wch: 16 },
      { wch: 16 }, { wch: 18 }, { wch: 14 }
    ];

    wsResumen['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } },
    ];

    const rangeResumen = XLSX.utils.decode_range(wsResumen['!ref']);
    for (let R = 0; R <= rangeResumen.e.r; R++) {
      for (let C = 0; C <= rangeResumen.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!wsResumen[cellRef]) continue;

        if (R === 0) {
          wsResumen[cellRef].s = {
            font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F2937' } },
            alignment: { horizontal: 'center', vertical: 'center' },
          };
        } else if (R === 1) {
          wsResumen[cellRef].s = {
            font: { italic: true, color: { rgb: '4B5563' } },
            alignment: { horizontal: 'center', vertical: 'center' },
          };
        } else if (R === 3) {
          wsResumen[cellRef].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '374151' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: {
              top: { style: 'thin', color: { rgb: 'D1D5DB' } },
              bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
              left: { style: 'thin', color: { rgb: 'D1D5DB' } },
              right: { style: 'thin', color: { rgb: 'D1D5DB' } },
            },
          };
        } else if (R === resumenData.length - 1) {
          wsResumen[cellRef].s = {
            font: { bold: true, color: { rgb: '1F2937' } },
            fill: { fgColor: { rgb: 'F3F4F6' } },
            alignment: { horizontal: C === 0 ? 'left' : 'center', vertical: 'center' },
            border: {
              top: { style: 'medium', color: { rgb: '1F2937' } },
              bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
              left: { style: 'thin', color: { rgb: 'D1D5DB' } },
              right: { style: 'thin', color: { rgb: 'D1D5DB' } },
            },
          };
        } else {
          wsResumen[cellRef].s = {
            alignment: { horizontal: C === 0 ? 'left' : 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'E5E7EB' } },
              bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
              left: { style: 'thin', color: { rgb: 'E5E7EB' } },
              right: { style: 'thin', color: { rgb: 'E5E7EB' } },
            },
          };
        }
      }
    }

    wsResumen['!rows'] = [{ hpt: 26 }, { hpt: 18 }, { hpt: 8 }, { hpt: 32 }];

    sheets.push({ name: 'Resumen Edificios', ws: wsResumen });

    // ====== HOJA 2: Cruce prendas vs edificios ======
    if (comparativoEdificios.prendas.length > 0 && comparativoEdificios.nombresEdificios.length > 0) {
      const headerRow1 = ['Prenda'];
      const headerRow2 = [''];
      const colWidths = [{ wch: 28 }];

      comparativoEdificios.nombresEdificios.forEach(nombre => {
        headerRow1.push(nombre, '', '');
        headerRow2.push('Entradas', 'Salidas', 'Saldo');
        colWidths.push({ wch: 11 }, { wch: 11 }, { wch: 11 });
      });

      const matrixData = [headerRow1, headerRow2];

      comparativoEdificios.prendas.forEach(row => {
        const r = [row.prenda];
        comparativoEdificios.nombresEdificios.forEach(nombre => {
          r.push(row[`${nombre}_Entradas`] || 0);
          r.push(row[`${nombre}_Salidas`] || 0);
          r.push(row[`${nombre}_Saldo`] || 0);
        });
        matrixData.push(r);
      });

      const wsPrendas = XLSX.utils.aoa_to_sheet(matrixData);
      wsPrendas['!cols'] = colWidths;

      const merges = [{ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }];
      comparativoEdificios.nombresEdificios.forEach((_, i) => {
        const startCol = 1 + i * 3;
        merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: startCol + 2 } });
      });
      wsPrendas['!merges'] = merges;

      const rangePrendas = XLSX.utils.decode_range(wsPrendas['!ref']);
      for (let R = 0; R <= 1; R++) {
        for (let C = 0; C <= rangePrendas.e.c; C++) {
          const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
          if (wsPrendas[cellRef]) {
            wsPrendas[cellRef].s = {
              font: { bold: true, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: R === 0 ? '1F2937' : '4B5563' } },
              alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
              border: {
                top: { style: 'thin', color: { rgb: 'D1D5DB' } },
                bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
                left: { style: 'thin', color: { rgb: 'D1D5DB' } },
                right: { style: 'thin', color: { rgb: 'D1D5DB' } },
              },
            };
          }
        }
      }

      wsPrendas['!rows'] = [{ hpt: 24 }, { hpt: 20 }];

      for (let R = 2; R <= rangePrendas.e.r; R++) {
        for (let C = 0; C <= rangePrendas.e.c; C++) {
          const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
          if (wsPrendas[cellRef]) {
            const isSaldo = (C - 1) % 3 === 2;
            wsPrendas[cellRef].s = {
              alignment: { horizontal: C === 0 ? 'left' : 'center', vertical: 'center' },
              font: isSaldo ? { bold: true } : {},
              fill: isSaldo ? { fgColor: { rgb: 'F9FAFB' } } : { fgColor: { rgb: 'FFFFFF' } },
              border: {
                top: { style: 'thin', color: { rgb: 'E5E7EB' } },
                bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
                left: { style: 'thin', color: { rgb: 'E5E7EB' } },
                right: { style: 'thin', color: { rgb: 'E5E7EB' } },
              },
            };
          }
        }
      }

      sheets.push({ name: 'Prendas vs Edificios', ws: wsPrendas });
    }

    return sheets;
  };

  const exportComparativoEdificiosToExcel = () => {
    try {
      if (!comparativoEdificios.resumen || comparativoEdificios.resumen.length === 0) {
        toast({
          title: 'Sin datos',
          description: 'No hay datos para exportar en el comparativo de edificios',
          variant: 'destructive',
        });
        return;
      }

      const wb = XLSX.utils.book_new();
      const sheets = buildComparativoEdificiosWorksheets();
      sheets.forEach(s => XLSX.utils.book_append_sheet(wb, s.ws, s.name));

      const fileName = `Comparativo_Edificios_Horizontal_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast({
        title: 'Exportación exitosa',
        description: `Archivo ${fileName} descargado`,
      });
    } catch (error) {
      console.error('Error al exportar comparativo:', error);
      toast({
        title: 'Error',
        description: 'No se pudo exportar el comparativo de edificios',
        variant: 'destructive',
      });
    }
  };

  const exportConsolidadoPrendasToExcel = () => {
    try {
      if (!Array.isArray(kardex) || kardex.length === 0) {
        toast({
          title: 'Sin datos',
          description: 'No hay datos para exportar en el consolidado de prendas',
          variant: 'destructive',
        });
        return;
      }

      const kardexData = kardex.map(item => ({
        'Prenda': item.nombre,
        'Peso Unitario (g)': item.pesoUnitario,
        'Cant Recolecciones': item.entradas,
        'Peso Recolecciones (kg)': Number((item.pesoTotalEntradas / 1000).toFixed(2)),
        'Cant Entregas': item.salidas,
        'Peso Entregado (kg)': Number((item.pesoTotalSalidas / 1000).toFixed(2)),
        'Saldo': item.saldo,
        'Peso Saldo (kg)': Number((item.pesoSaldo / 1000).toFixed(2)),
      }));

      kardexData.push({
        'Prenda': 'TOTALES',
        'Peso Unitario (g)': '-',
        'Cant Recolecciones': kardex.reduce((sum, item) => sum + item.entradas, 0),
        'Peso Recolecciones (kg)': Number((kardex.reduce((sum, item) => sum + item.pesoTotalEntradas, 0) / 1000).toFixed(2)),
        'Cant Entregas': kardex.reduce((sum, item) => sum + item.salidas, 0),
        'Peso Entregado (kg)': Number((kardex.reduce((sum, item) => sum + item.pesoTotalSalidas, 0) / 1000).toFixed(2)),
        'Saldo': kardex.reduce((sum, item) => sum + item.saldo, 0),
        'Peso Saldo (kg)': Number((kardex.reduce((sum, item) => sum + item.pesoSaldo, 0) / 1000).toFixed(2)),
      });

      const wb = XLSX.utils.book_new();
      const wsKardex = XLSX.utils.json_to_sheet(kardexData);
      XLSX.utils.book_append_sheet(wb, wsKardex, 'Consolidado Prendas');

      const fileName = `Consolidado_Prendas_Horizontal_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast({
        title: 'Exportación exitosa',
        description: `Archivo ${fileName} descargado`,
      });
    } catch (error) {
      console.error('Error al exportar consolidado:', error);
      toast({
        title: 'Error',
        description: 'No se pudo exportar el consolidado de prendas',
        variant: 'destructive',
      });
    }
  };

  const exportToExcel = () => {
    try {
      const entregas = entregas.filter(e => e.estado === 'entregado');
      const recolecciones = entregas.filter(e => e.estado === 'recoleccion');

      // Hoja de Resumen
      const resumenData = [
        ['REPORTE DE ENTREGAS HORIZONTALES'],
        [''],
        ['Período:', `${filters.fechaInicio} a ${filters.fechaFin}`],
        ['Edificio:', filters.edificioId ? edificios.find(e => e._id === filters.edificioId)?.nombre : 'Todos'],
        ['Activo:', filters.hospedajeId ? hospedajes.find(h => h._id === filters.hospedajeId)?.nombrePropiedad : 'Todos'],
        [''],
        ['RESUMEN GENERAL'],
        ['Total Entregas:', stats.totalEntregas],
        ['Total Recolecciones:', stats.totalRecolecciones],
        ['Peso Total Entregado (g):', stats.pesoTotalEntregado],
        ['Peso Total Recolectado (g):', stats.pesoTotalRecolectado],
        ['Items Totales Entregados:', stats.itemsTotalesEntregados],
        ['Items Totales Recolectados:', stats.itemsTotalesRecolectados],
      ];

      // Hoja de Entregas Detalladas
      const entregasData = entregas.map(e => ({
        'Consecutivo': e.consecutivo_entrega || 'N/A',
        'Fecha Entrega': e.fecha_entrega ? format(new Date(e.fecha_entrega), 'dd/MM/yyyy HH:mm') : 'N/A',
        'Edificio': e.edificio?.nombre || 'N/A',
        'Activo': e.hospedaje?.nombre || 'N/A',
        'Solicitante': e.solicitante?.nombre || 'N/A',
        'Conductor': e.conductor?.nombre || 'N/A',
        'Receptor': e.receptor_nombre || 'N/A',
        'Total Items': e.total_items || 0,
        'Peso Total (g)': e.peso_total || 0,
        'Observaciones': e.observaciones || ''
      }));

      // Hoja de Recolecciones Detalladas
      const recoleccionesData = recolecciones.map(e => ({
        'Consecutivo': e.consecutivo_recoleccion || 'N/A',
        'Fecha Recolección': e.fecha_entrega ? format(new Date(e.fecha_entrega), 'dd/MM/yyyy HH:mm') : 'N/A',
        'Edificio': e.edificio?.nombre || 'N/A',
        'Activo': e.hospedaje?.nombre || 'N/A',
        'Solicitante': e.solicitante?.nombre || 'N/A',
        'Conductor': e.conductor?.nombre || 'N/A',
        'Receptor': e.receptor_nombre || 'N/A',
        'Total Items': e.total_items || 0,
        'Peso Total (g)': e.peso_total || 0,
        'Observaciones': e.observaciones || ''
      }));

      // Hoja de Items Detallados
      const itemsData = [];
      entregas.forEach(entrega => {
        entrega.items?.forEach(item => {
          itemsData.push({
            'Tipo': entrega.estado === 'entregado' ? 'Entrega' : 'Recolección',
            'Consecutivo': entrega.consecutivo_entrega || entrega.consecutivo_recoleccion || 'N/A',
            'Fecha': entrega.fecha_entrega ? format(new Date(entrega.fecha_entrega), 'dd/MM/yyyy') : 'N/A',
            'Edificio': entrega.edificio?.nombre || 'N/A',
            'Activo': entrega.hospedaje?.nombre || 'N/A',
            'Item': item.nombre,
            'Cantidad': item.cantidad,
            'Peso Unitario (g)': item.peso,
            'Peso Total (g)': item.cantidad * item.peso,
            'Observación': item.observacion || ''
          });
        });
      });

      // Crear libro de trabajo
      const wb = XLSX.utils.book_new();

      const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

      if (entregasData.length > 0) {
        const wsEntregas = XLSX.utils.json_to_sheet(entregasData);
        XLSX.utils.book_append_sheet(wb, wsEntregas, 'Entregas');
      }

      if (recoleccionesData.length > 0) {
        const wsRecolecciones = XLSX.utils.json_to_sheet(recoleccionesData);
        XLSX.utils.book_append_sheet(wb, wsRecolecciones, 'Recolecciones');
      }

      if (itemsData.length > 0) {
        const wsItems = XLSX.utils.json_to_sheet(itemsData);
        XLSX.utils.book_append_sheet(wb, wsItems, 'Items Detallados');
      }

      // Hoja de Kardex de Prendas
      if (kardex.length > 0) {
        const kardexData = kardex.map(item => ({
          'Prenda': item.nombre,
          'Peso Unitario (g)': item.pesoUnitario,
          'Entradas (Recolecciones)': item.entradas,
          'Peso Total Entradas (kg)': (item.pesoTotalEntradas / 1000).toFixed(2),
          'Salidas (Entregas)': item.salidas,
          'Peso Total Salidas (kg)': (item.pesoTotalSalidas / 1000).toFixed(2),
          'Saldo': item.saldo,
          'Peso Saldo (kg)': (item.pesoSaldo / 1000).toFixed(2)
        }));

        // Agregar fila de totales
        kardexData.push({
          'Prenda': 'TOTALES',
          'Peso Unitario (g)': '-',
          'Entradas (Recolecciones)': kardex.reduce((sum, item) => sum + item.entradas, 0),
          'Peso Total Entradas (kg)': (kardex.reduce((sum, item) => sum + item.pesoTotalEntradas, 0) / 1000).toFixed(2),
          'Salidas (Entregas)': kardex.reduce((sum, item) => sum + item.salidas, 0),
          'Peso Total Salidas (kg)': (kardex.reduce((sum, item) => sum + item.pesoTotalSalidas, 0) / 1000).toFixed(2),
          'Saldo': kardex.reduce((sum, item) => sum + item.saldo, 0),
          'Peso Saldo (kg)': (kardex.reduce((sum, item) => sum + item.pesoSaldo, 0) / 1000).toFixed(2)
        });

        const wsKardex = XLSX.utils.json_to_sheet(kardexData);
        XLSX.utils.book_append_sheet(wb, wsKardex, 'Kardex de Prendas');
      }

      // Hoja de Kardex Detallado (Matriz)
      if (kardexDetallado.prendas && kardexDetallado.prendas.length > 0) {
        const headerRow1 = ['Prenda'];
        const headerRow2 = [''];
        const colWidths = [{ wch: 30 }];

        kardexDetallado.transacciones.forEach(trans => {
          const tipoLabel = trans.tipo === 'entrega' ? 'Entrega' : 'Recolección';
          const codigo = `${trans.tipo === 'entrega' ? 'E' : 'R'}-${trans.consecutivo || 'N/A'}`;
          headerRow1.push(`${tipoLabel}\n${codigo}`);
          const subHeader = [
            trans.edificio || 'Sin edificio',
            trans.hospedaje || 'Sin activo',
            format(new Date(trans.fecha), 'dd/MM/yyyy'),
          ].filter(Boolean).join('\n');
          headerRow2.push(subHeader);
          colWidths.push({ wch: 24 });
        });
        headerRow1.push('Saldo Final');
        headerRow2.push('');
        colWidths.push({ wch: 14 });

        const matrixData = [headerRow1, headerRow2];

        kardexDetallado.prendas.forEach(row => {
          const rowData = [row.prenda];
          row.transacciones.forEach(trans => {
            if (trans.movimiento !== 0) {
              rowData.push(`${trans.movimiento > 0 ? '+' : ''}${trans.movimiento} (${trans.saldo})`);
            } else {
              rowData.push('-');
            }
          });
          rowData.push(row.saldoFinal);
          matrixData.push(rowData);
        });

        const wsKardexDetallado = XLSX.utils.aoa_to_sheet(matrixData);
        wsKardexDetallado['!cols'] = colWidths;

        const merges = [
          { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Prenda
          { s: { r: 0, c: headerRow1.length - 1 }, e: { r: 1, c: headerRow1.length - 1 } }, // Saldo Final
        ];
        wsKardexDetallado['!merges'] = merges;

        const range = XLSX.utils.decode_range(wsKardexDetallado['!ref']);
        for (let R = 0; R <= 1; R++) {
          for (let C = 0; C <= range.e.c; C++) {
            const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
            if (wsKardexDetallado[cellRef]) {
              wsKardexDetallado[cellRef].s = {
                font: { bold: true, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '1F2937' } },
                alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                border: {
                  top: { style: 'thin', color: { rgb: 'D1D5DB' } },
                  bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
                  left: { style: 'thin', color: { rgb: 'D1D5DB' } },
                  right: { style: 'thin', color: { rgb: 'D1D5DB' } },
                },
              };
            }
          }
        }

        wsKardexDetallado['!rows'] = [{ hpt: 30 }, { hpt: 30 }];

        for (let R = 2; R <= range.e.r; R++) {
          for (let C = 0; C <= range.e.c; C++) {
            const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
            if (wsKardexDetallado[cellRef]) {
              wsKardexDetallado[cellRef].s = {
                alignment: { horizontal: C === 0 ? 'left' : 'center', vertical: 'center', wrapText: true },
                border: {
                  top: { style: 'thin', color: { rgb: 'E5E7EB' } },
                  bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
                  left: { style: 'thin', color: { rgb: 'E5E7EB' } },
                  right: { style: 'thin', color: { rgb: 'E5E7EB' } },
                },
              };
            }
          }
        }

        XLSX.utils.book_append_sheet(wb, wsKardexDetallado, 'Kardex Detallado');
      }

      // Hoja de Comparativo por Edificio
      if (comparativoEdificios.resumen && comparativoEdificios.resumen.length > 1) {
        const comparativoSheets = buildComparativoEdificiosWorksheets();
        comparativoSheets.forEach(s => XLSX.utils.book_append_sheet(wb, s.ws, s.name));
      }

      // Guardar archivo
      const fileName = `Reporte_Entregas_Horizontal_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast({
        title: 'Exportación exitosa',
        description: `Archivo ${fileName} descargado`,
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      toast({
        title: 'Error',
        description: 'No se pudo exportar el reporte',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className=" space-y-6">
      {/* Header */}
      <Card className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>

            <h1 className="text-3xl font-bold text-gray-900">
              Reporte de Entregas Horizontales
            </h1>
            <p className="text-gray-600 mt-1">
              Análisis de entregas y recolecciones de lencería
            </p>
          </div>
        </div>
      </Card>

      {/* Filtros */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Fecha Inicio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.fechaInicio}
              onChange={(e) => handleFilterChange('fechaInicio', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Fecha Fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.fechaFin}
              onChange={(e) => handleFilterChange('fechaFin', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Edificio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Edificio
            </label>
            <select
              value={filters.edificioId}
              onChange={(e) => handleFilterChange('edificioId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los edificios</option>
              {edificios.map(edificio => (
                <option key={edificio._id} value={edificio._id}>
                  {edificio.nombre} - {edificio.ciudad}
                </option>
              ))}
            </select>
          </div>

          {/* Activo/Hospedaje */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Home className="w-4 h-4 inline mr-1" />
              Activo
            </label>
            <select
              value={filters.hospedajeId}
              onChange={(e) => handleFilterChange('hospedajeId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!filters.edificioId}
            >
              <option value="">Todos los activos</option>
              {filteredHospedajes.map(hospedaje => (
                <option key={hospedaje._id} value={hospedaje._id}>
                  {hospedaje.nombrePropiedad}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={loadEntregas} disabled={loading}>
            {loading ? 'Cargando...' : 'Generar Reporte'}
          </Button>
        </div>
      </Card>

      {/* Estadísticas */}
      {entregas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Entregas */}
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Entregas</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{stats.totalEntregas}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="mt-4 space-y-1 text-sm text-gray-600">
              <p>Items: {stats.itemsTotalesEntregados}</p>
              <p>Peso: {(stats.pesoTotalEntregado / 1000).toFixed(2)} kg</p>
            </div>
          </Card>

          {/* Recolecciones */}
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Recolecciones</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">{stats.totalRecolecciones}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <TrendingDown className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 space-y-1 text-sm text-gray-600">
              <p>Items: {stats.itemsTotalesRecolectados}</p>
              <p>Peso: {(stats.pesoTotalRecolectado / 1000).toFixed(2)} kg</p>
            </div>
          </Card>

          {/* Balance */}
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Balance Neto</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">
                  {stats.totalEntregas - stats.totalRecolecciones}
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Package className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 space-y-1 text-sm text-gray-600">
              <p>Items: {stats.itemsTotalesEntregados - stats.itemsTotalesRecolectados}</p>
              <p>Peso: {((stats.pesoTotalEntregado - stats.pesoTotalRecolectado) / 1000).toFixed(2)} kg</p>
            </div>
          </Card>
        </div>
      )}

      {/* Kardex de Prendas */}
      {kardex.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Consolidado de Prendas (Recoleciones y Entregas)</h2>
            <Button
              onClick={exportConsolidadoPrendasToExcel}
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
              disabled={kardex.length === 0}
            >
              <FileSpreadsheet className="w-4 h-4" />
              Exportar Consolidado
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prenda</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Peso Unit. (g)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">cant Recolecciones </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Peso Recolecciones (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">cant. entregas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Peso Entregado (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Peso Saldo (kg)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {kardex.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.nombre}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">{item.pesoUnitario}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-blue-600">{item.entradas}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">
                      {(item.pesoTotalEntradas / 1000).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-green-600">{item.salidas}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">
                      {(item.pesoTotalSalidas / 1000).toFixed(2)}
                    </td>
                    <td className={`px-4 py-3 text-right text-sm font-bold ${item.saldo > 0 ? 'text-blue-600' : item.saldo < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                      {item.saldo > 0 ? '+' : ''}{item.saldo}
                    </td>
                    <td className={`px-4 py-3 text-right text-sm font-medium ${item.pesoSaldo > 0 ? 'text-blue-600' : item.pesoSaldo < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                      {item.pesoSaldo > 0 ? '+' : ''}{(item.pesoSaldo / 1000).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-bold">
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-900">TOTALES</td>
                  <td className="px-4 py-3 text-right text-sm">-</td>
                  <td className="px-4 py-3 text-right text-sm text-blue-600">
                    {kardex.reduce((sum, item) => sum + item.entradas, 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {(kardex.reduce((sum, item) => sum + item.pesoTotalEntradas, 0) / 1000).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-green-600">
                    {kardex.reduce((sum, item) => sum + item.salidas, 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {(kardex.reduce((sum, item) => sum + item.pesoTotalSalidas, 0) / 1000).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {kardex.reduce((sum, item) => sum + item.saldo, 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {(kardex.reduce((sum, item) => sum + item.pesoSaldo, 0) / 1000).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Kardex Detallado (Matriz) */}
      {kardexDetallado.prendas && kardexDetallado.prendas.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Kardex Detallado por Transacción</h2>
              <p className="text-sm text-gray-600 mt-1">
                Valores positivos (+) indican recolecciones (entradas), valores negativos (-) indican entregas (salidas)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={exportKardexDetalladoToExcel}
                className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                disabled={!kardexDetallado?.prendas || kardexDetallado.prendas.length === 0}
              >
                <FileSpreadsheet className="w-4 h-4" />
                Exportar Kardex
              </Button>
              <Button
                onClick={() => setShowKardexModal(true)}
                variant="outline"
                className="gap-2"
              >
                <Maximize2 className="w-4 h-4" />
                Ver en Pantalla Completa
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border border-gray-300 sticky left-0 bg-gray-50 z-10">
                    Prenda
                  </th>
                  {kardexDetallado.transacciones.map((trans, index) => (
                    <th key={trans.id} className="px-2 py-2 text-center font-medium text-gray-700 border border-gray-300 min-w-[80px]">
                      <div className="flex flex-col items-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full mb-1 ${trans.tipo === 'entrega'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}>
                          {trans.tipo === 'entrega' ? 'E' : 'R'}
                        </span>
                        <span className="text-xs font-semibold">{trans.consecutivo}</span>
                        <span className="text-xs text-gray-500">
                          {format(new Date(trans.fecha), 'dd/MM')}
                        </span>
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center font-medium text-gray-700 border border-gray-300 bg-gray-100 sticky right-0 z-10">
                    Saldo Final
                  </th>
                </tr>
              </thead>
              <tbody>
                {kardexDetallado.prendas.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-left font-medium text-gray-900 border border-gray-300 sticky left-0 bg-white z-10">
                      {row.prenda}
                    </td>
                    {row.transacciones.map((trans, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-2 py-2 text-center border border-gray-300 ${trans.movimiento > 0
                            ? 'bg-blue-50 text-blue-700 font-semibold'
                            : trans.movimiento < 0
                              ? 'bg-green-50 text-green-700 font-semibold'
                              : 'bg-gray-50 text-gray-400'
                          }`}
                        title={`${trans.tipo === 'entrega' ? 'Entrega' : 'Recolección'}: ${trans.consecutivo}\n${trans.edificio} - ${trans.hospedaje}\nSaldo: ${trans.saldo}`}
                      >
                        {trans.movimiento !== 0 ? (
                          <div className="flex flex-col items-center">
                            <span className="font-bold">
                              {trans.movimiento > 0 ? '+' : ''}{trans.movimiento}
                            </span>
                            <span className="text-xs text-gray-600">
                              ({trans.saldo})
                            </span>
                          </div>
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                    ))}
                    <td className={`px-3 py-2 text-center font-bold border border-gray-300 sticky right-0 z-10 ${row.saldoFinal > 0
                        ? 'bg-blue-100 text-blue-700'
                        : row.saldoFinal < 0
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                      {row.saldoFinal > 0 ? '+' : ''}{row.saldoFinal}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-xs text-gray-600">
            <p><strong>Leyenda:</strong></p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li><span className="font-semibold text-blue-700">Azul (+):</span> Recolecciones (entradas al inventario)</li>
              <li><span className="font-semibold text-green-700">Verde (-):</span> Entregas (salidas del inventario)</li>
              <li><span className="text-gray-600">Número entre paréntesis:</span> Saldo acumulado después de la transacción</li>
            </ul>
          </div>
        </Card>
      )}

      {/* Comparativo por Edificio */}
      {comparativoEdificios.resumen && comparativoEdificios.resumen.length > 1 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Comparativo por Edificio</h2>
              <p className="text-sm text-gray-600 mt-1">
                Resumen de movimientos y cruce de prendas por edificio dentro del período seleccionado
              </p>
            </div>
            <Button
              onClick={exportComparativoEdificiosToExcel}
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
              disabled={!comparativoEdificios.resumen || comparativoEdificios.resumen.length === 0}
            >
              <FileSpreadsheet className="w-4 h-4" />
              Exportar Comparativo
            </Button>
          </div>

          {/* Tabla Resumen por Edificio */}
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Resumen general</h3>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Edificio</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Entregas</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Recolecciones</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Saldo Trans.</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Peso Entregado (kg)</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Peso Recolectado (kg)</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Saldo Peso (kg)</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Items Entregados</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Items Recolectados</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {comparativoEdificios.resumen.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{r.nombre}</td>
                    <td className="px-3 py-2 text-right text-green-600 font-medium">{r.totalEntregas}</td>
                    <td className="px-3 py-2 text-right text-blue-600 font-medium">{r.totalRecolecciones}</td>
                    <td className={`px-3 py-2 text-right font-bold ${r.saldoEntregas > 0 ? 'text-red-600' : r.saldoEntregas < 0 ? 'text-blue-600' : 'text-gray-600'}`}>
                      {r.saldoEntregas > 0 ? '+' : ''}{r.saldoEntregas}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">{(r.pesoTotalEntregado / 1000).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{(r.pesoTotalRecolectado / 1000).toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${r.saldoPeso > 0 ? 'text-red-600' : r.saldoPeso < 0 ? 'text-blue-600' : 'text-gray-600'}`}>
                      {r.saldoPeso > 0 ? '+' : ''}{(r.saldoPeso / 1000).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">{r.itemsTotalesEntregados}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{r.itemsTotalesRecolectados}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-bold">
                <tr>
                  <td className="px-3 py-2 text-gray-900">TOTALES</td>
                  <td className="px-3 py-2 text-right text-green-700">
                    {comparativoEdificios.resumen.reduce((s, r) => s + r.totalEntregas, 0)}
                  </td>
                  <td className="px-3 py-2 text-right text-blue-700">
                    {comparativoEdificios.resumen.reduce((s, r) => s + r.totalRecolecciones, 0)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900">
                    {comparativoEdificios.resumen.reduce((s, r) => s + r.saldoEntregas, 0)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900">
                    {(comparativoEdificios.resumen.reduce((s, r) => s + r.pesoTotalEntregado, 0) / 1000).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900">
                    {(comparativoEdificios.resumen.reduce((s, r) => s + r.pesoTotalRecolectado, 0) / 1000).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900">
                    {(comparativoEdificios.resumen.reduce((s, r) => s + r.saldoPeso, 0) / 1000).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900">
                    {comparativoEdificios.resumen.reduce((s, r) => s + r.itemsTotalesEntregados, 0)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900">
                    {comparativoEdificios.resumen.reduce((s, r) => s + r.itemsTotalesRecolectados, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Tabla Cruce: Prendas vs Edificios */}
          {comparativoEdificios.prendas.length > 0 && comparativoEdificios.nombresEdificios.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Cruce prendas vs edificios</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border border-gray-300 sticky left-0 bg-gray-50 z-10 min-w-[180px]">
                        Prenda
                      </th>
                      {comparativoEdificios.nombresEdificios.map(nombre => (
                        <th key={nombre} colSpan={3} className="px-2 py-2 text-center font-medium text-gray-700 border border-gray-300">
                          {nombre}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border border-gray-300 sticky left-0 bg-gray-50 z-10">
                        &nbsp;
                      </th>
                      {comparativoEdificios.nombresEdificios.map(nombre => (
                        <React.Fragment key={nombre}>
                          <th className="px-2 py-1 text-right text-[10px] font-medium text-blue-700 border border-gray-300">Entradas</th>
                          <th className="px-2 py-1 text-right text-[10px] font-medium text-green-700 border border-gray-300">Salidas</th>
                          <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-700 border border-gray-300 bg-gray-100">Saldo</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparativoEdificios.prendas.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-900 border border-gray-300 sticky left-0 bg-white z-10">
                          {row.prenda}
                        </td>
                        {comparativoEdificios.nombresEdificios.map(nombre => {
                          const entradas = row[`${nombre}_Entradas`] || 0;
                          const salidas = row[`${nombre}_Salidas`] || 0;
                          const saldo = entradas - salidas;
                          return (
                            <React.Fragment key={nombre}>
                              <td className="px-2 py-1 text-right text-blue-700 border border-gray-300">{entradas}</td>
                              <td className="px-2 py-1 text-right text-green-700 border border-gray-300">{salidas}</td>
                              <td className={`px-2 py-1 text-right font-bold border border-gray-300 bg-gray-50 ${saldo > 0 ? 'text-blue-700' : saldo < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                                {saldo > 0 ? '+' : ''}{saldo}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Modal Kardex Detallado Pantalla Completa */}
      {showKardexModal && kardexDetallado.prendas && kardexDetallado.prendas.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full h-full max-w-[98vw] max-h-[98vh] flex flex-col">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Kardex Detallado por Transacción</h2>
                <p className="text-xs text-gray-600 mt-1">
                  Valores positivos (+) indican recolecciones (entradas), valores negativos (-) indican entregas (salidas)
                </p>
              </div>
              <Button
                onClick={() => setShowKardexModal(false)}
                variant="ghost"
                size="sm"
                className="gap-2"
              >
                <X className="w-5 h-5" />
                Cerrar
              </Button>
            </div>

            {/* Contenido del Modal con Scroll */}
            <div className="flex-1 overflow-auto p-4">
              <div className="relative">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-gray-50 sticky top-0 z-20">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border border-gray-300 sticky left-0 bg-gray-50 z-30 min-w-[180px]">
                        Prenda
                      </th>
                      {kardexDetallado.transacciones.map((trans, index) => (
                        <th key={trans.id} className="px-2 py-2 text-center font-medium text-gray-700 border border-gray-300 min-w-[90px]">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${trans.tipo === 'entrega' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                              {trans.tipo === 'entrega' ? 'Entrega' : 'Recolección'}
                            </span>
                            <span className="text-xs font-bold">{trans.consecutivo}</span>
                            <span className="text-[10px] text-gray-500">
                              {format(new Date(trans.fecha), 'dd/MM/yyyy')}
                            </span>
                            <span className="text-[10px] text-gray-600 font-medium">
                              {trans.edificio}
                            </span>
                            <span className="text-[10px] text-gray-500">
                              {trans.hospedaje}
                            </span>
                          </div>
                        </th>
                      ))}
                      <th className="px-3 py-2 text-center font-semibold text-gray-700 border border-gray-300 bg-gray-100 sticky right-0 z-30 min-w-[100px]">
                        Saldo Final
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {kardexDetallado.prendas.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-left font-semibold text-gray-900 border border-gray-300 sticky left-0 bg-white z-20">
                          {row.prenda}
                        </td>
                        {row.transacciones.map((trans, colIndex) => (
                          <td
                            key={colIndex}
                            className={`px-2 py-2 text-center border border-gray-300 ${trans.movimiento > 0
                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                : trans.movimiento < 0
                                  ? 'bg-green-50 text-green-700 font-semibold'
                                  : 'bg-gray-50 text-gray-400'
                              }`}
                          >
                            {trans.movimiento !== 0 ? (
                              <div className="flex flex-col items-center">
                                <span className="text-sm font-bold">
                                  {trans.movimiento > 0 ? '+' : ''}{trans.movimiento}
                                </span>
                                <span className="text-[10px] text-gray-600 mt-0.5">
                                  Saldo: {trans.saldo}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        ))}
                        <td className={`px-3 py-2 text-center font-bold text-sm border border-gray-300 sticky right-0 z-20 ${row.saldoFinal > 0
                            ? 'bg-blue-100 text-blue-700'
                            : row.saldoFinal < 0
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                          {row.saldoFinal > 0 ? '+' : ''}{row.saldoFinal}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer con Leyenda */}
            <div className="p-4 border-t bg-gray-50">
              <p className="font-semibold text-gray-900 mb-2 text-xs">Leyenda:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
                  <span><span className="font-semibold text-blue-700">Azul (+):</span> Recolecciones (entradas al inventario)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                  <span><span className="font-semibold text-green-700">Verde (-):</span> Entregas (salidas del inventario)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
                  <span><span className="font-semibold text-gray-700">Saldo:</span> Saldo acumulado después de cada transacción</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabla de Entregas */}
      {entregas.length > 0 && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Detalle de Entregas y Recolecciones</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consecutivo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Edificio</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conductor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receptor</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Peso (kg)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entregas.map((entrega, index) => {
                  const isExpanded = expandedRows.has(entrega._id);
                  return (
                    <React.Fragment key={entrega._id || index}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleRowExpansion(entrega._id)}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${entrega.estado === 'entregado'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                            }`}>
                            {entrega.estado === 'entregado' ? 'Entrega' : 'Recolección'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {entrega.consecutivo_entrega || entrega.consecutivo_recoleccion || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {entrega.fecha_entrega ? format(new Date(entrega.fecha_entrega), 'dd/MM/yyyy HH:mm') : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm">{entrega.edificio?.nombre || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">{entrega.hospedaje?.nombre || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">{entrega.conductor?.nombre || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">{entrega.receptor_nombre || 'N/A'}</td>
                        <td className="px-4 py-3 text-right text-sm font-medium">{entrega.total_items || 0}</td>
                        <td className="px-4 py-3 text-right text-sm font-medium">
                          {((entrega.peso_total || 0) / 1000).toFixed(2)}
                        </td>
                      </tr>
                      {isExpanded && entrega.items && entrega.items.length > 0 && (
                        <tr>
                          <td colSpan="10" className="px-4 py-3 bg-gray-50">
                            <div className="ml-8">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Detalle de Items:</h4>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-gray-300">
                                    <th className="text-left py-2 px-3 font-medium text-gray-600">Prenda</th>
                                    <th className="text-right py-2 px-3 font-medium text-gray-600">Cantidad</th>
                                    <th className="text-right py-2 px-3 font-medium text-gray-600">Peso Unit. (g)</th>
                                    <th className="text-right py-2 px-3 font-medium text-gray-600">Peso Total (kg)</th>
                                    <th className="text-left py-2 px-3 font-medium text-gray-600">Observación</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {entrega.items.map((item, itemIndex) => (
                                    <tr key={itemIndex} className="border-b border-gray-200">
                                      <td className="py-2 px-3 text-gray-800">{item.nombre}</td>
                                      <td className="py-2 px-3 text-right font-medium text-gray-900">{item.cantidad}</td>
                                      <td className="py-2 px-3 text-right text-gray-600">{item.peso}</td>
                                      <td className="py-2 px-3 text-right font-medium text-gray-900">
                                        {((item.cantidad * item.peso) / 1000).toFixed(2)}
                                      </td>
                                      <td className="py-2 px-3 text-gray-600 italic">{item.observacion || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-gray-100 font-bold">
                                  <tr>
                                    <td className="py-2 px-3">TOTAL</td>
                                    <td className="py-2 px-3 text-right">{entrega.total_items}</td>
                                    <td className="py-2 px-3 text-right">-</td>
                                    <td className="py-2 px-3 text-right">{((entrega.peso_total || 0) / 1000).toFixed(2)}</td>
                                    <td className="py-2 px-3"></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Estado vacío */}
      {!loading && entregas.length === 0 && (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay datos para mostrar
          </h3>
          <p className="text-gray-600">
            Selecciona los filtros y haz clic en "Generar Reporte" para ver los resultados
          </p>
        </Card>
      )}
    </div>
  );
};

export default ReporteEntregasHorizontal;
